/**
 * Seed Management Component
 * Admin-only component for managing database seeding operations
 */

import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import {
    Database,
    BookOpen,
    Globe2,
    GraduationCap,
    Briefcase,
    Search,
    RefreshCw,
    Loader2,
    CheckCircle,
    AlertTriangle,
    Play,
    Server
} from 'lucide-react';

const SeedManagement = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState({});
    const [status, setStatus] = useState({});

    // Check if user is admin
    if (user?.role !== 'admin') {
        return null;
    }

    const seedOperations = [
        {
            id: 'playbooks',
            name: 'R-PAT Playbooks',
            description: 'Seed training playbooks with chapters and content pages',
            icon: BookOpen,
            endpoint: '/rpat/admin/seed-playbooks',
            color: 'text-violet-500',
            bgColor: 'bg-violet-500/10'
        },
        {
            id: 'global_modules',
            name: 'Global Compliance Topics',
            description: 'Seed compliance training topics for US, UK, AU, CA, DE',
            icon: Globe2,
            endpoint: '/rpat/global/admin/seed',
            color: 'text-blue-500',
            bgColor: 'bg-blue-500/10'
        },
        {
            id: 'rpat_courses',
            name: 'R-PAT Course Topics',
            description: 'Seed main R-PAT course with phases and topics',
            icon: GraduationCap,
            endpoint: '/rpat/admin/seed-courses',
            color: 'text-emerald-500',
            bgColor: 'bg-emerald-500/10'
        },
        {
            id: 'job_skills',
            name: 'Job Simulation Skills',
            description: 'Seed job skills for IT, Engineering, Healthcare verticals',
            icon: Briefcase,
            endpoint: '/rpat/admin/seed-job-skills',
            color: 'text-purple-500',
            bgColor: 'bg-purple-500/10'
        },
        {
            id: 'sourcing_labs',
            name: 'Sourcing Labs',
            description: 'Seed sourcing lab exercises and challenges',
            icon: Search,
            endpoint: '/rpat/admin/seed-sourcing-labs',
            color: 'text-amber-500',
            bgColor: 'bg-amber-500/10'
        }
    ];

    const runSeed = async (operation) => {
        setLoading(prev => ({ ...prev, [operation.id]: true }));
        setStatus(prev => ({ ...prev, [operation.id]: null }));

        try {
            const response = await api.post(operation.endpoint);
            const data = response.data;
            
            setStatus(prev => ({
                ...prev,
                [operation.id]: {
                    success: true,
                    message: data.message || 'Seeded successfully',
                    details: data
                }
            }));
            
            toast.success(`${operation.name}: ${data.message || 'Seeded successfully'}`);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.message || 'Seeding failed';
            setStatus(prev => ({
                ...prev,
                [operation.id]: {
                    success: false,
                    message: errorMsg
                }
            }));
            toast.error(`${operation.name}: ${errorMsg}`);
        } finally {
            setLoading(prev => ({ ...prev, [operation.id]: false }));
        }
    };

    const runAllSeeds = async () => {
        setLoading(prev => ({ ...prev, all: true }));
        
        for (const operation of seedOperations) {
            await runSeed(operation);
        }
        
        setLoading(prev => ({ ...prev, all: false }));
        toast.success('All seed operations completed!');
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary" />
                    Seed Management
                </CardTitle>
                <CardDescription>
                    Initialize or refresh database content for training modules. 
                    Use after deployment or when content needs to be reset.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Run All Seeds Button */}
                <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                            <Server className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <p className="font-medium">Run All Seeds</p>
                            <p className="text-sm text-muted-foreground">
                                Execute all seed operations in sequence
                            </p>
                        </div>
                    </div>
                    <Button 
                        onClick={runAllSeeds}
                        disabled={loading.all}
                        data-testid="seed-all-btn"
                    >
                        {loading.all ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Play className="h-4 w-4 mr-2" />
                        )}
                        Run All
                    </Button>
                </div>

                <Separator />

                {/* Individual Seed Operations */}
                <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground">Individual Operations</h3>
                    
                    {seedOperations.map((operation) => {
                        const Icon = operation.icon;
                        const operationStatus = status[operation.id];
                        const isLoading = loading[operation.id];
                        
                        return (
                            <div 
                                key={operation.id}
                                className="flex items-center justify-between p-4 rounded-lg border hover:border-primary/50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${operation.bgColor}`}>
                                        <Icon className={`h-5 w-5 ${operation.color}`} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium">{operation.name}</p>
                                            {operationStatus && (
                                                <Badge 
                                                    className={operationStatus.success 
                                                        ? 'bg-emerald-500' 
                                                        : 'bg-red-500'
                                                    }
                                                >
                                                    {operationStatus.success ? (
                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                    ) : (
                                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                                    )}
                                                    {operationStatus.success ? 'Done' : 'Failed'}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {operation.description}
                                        </p>
                                        {operationStatus?.success && operationStatus.details && (
                                            <p className="text-xs text-emerald-600 mt-1">
                                                {formatDetails(operation.id, operationStatus.details)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={() => runSeed(operation)}
                                    disabled={isLoading || loading.all}
                                    data-testid={`seed-${operation.id}-btn`}
                                >
                                    {isLoading ? (
                                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4 mr-1" />
                                    )}
                                    Seed
                                </Button>
                            </div>
                        );
                    })}
                </div>

                {/* Info */}
                <div className="p-4 rounded-lg border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Note:</strong> Seeding operations are idempotent - running them multiple times 
                        will not create duplicate data. Existing records are skipped.
                    </p>
                </div>
            </CardContent>
        </Card>
    );
};

// Helper function to format seed details
function formatDetails(operationId, details) {
    switch (operationId) {
        case 'playbooks':
            return `Created: ${details.playbooks_created || 0} playbooks, ${details.total_pages || 0} pages`;
        case 'global_modules':
            return `Topics: ${details.total_topics || 0}, Assessments: ${details.total_assessments || 0}`;
        case 'rpat_courses':
            return details.message || 'Courses seeded';
        case 'job_skills':
            return `Skills: ${details.skills_created || 0}`;
        case 'sourcing_labs':
            return `Labs: ${details.labs_created || 0}`;
        default:
            return details.message || 'Completed';
    }
}

export default SeedManagement;
